import React, { useState, useEffect } from 'react';
import './CookiesPolicy.css';
import { motion } from 'framer-motion';
import { FaCookieBite, FaInfoCircle, FaChartPie, FaCogs, FaShieldAlt } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { safeFetch } from '../utils/apiUtils';
import useSEO, { SITE_URL } from '../utils/useSEO';

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const getIcon = (type) => {
    switch (type) {
        case 'cookie': return <FaCookieBite />;
        case 'info': return <FaInfoCircle />;
        case 'chart': return <FaChartPie />;
        case 'cogs': return <FaCogs />;
        case 'shield': return <FaShieldAlt />;
        default: return <FaCookieBite />;
    }
};

const CookiesPolicy = () => {
    useSEO({
        title:       "Cookie Policy | Nimai Solutions",
        description: "Learn how Nimai Solutions uses cookies to improve your browsing experience. Understand what cookies we use and how to manage your preferences.",
        canonical:   `${SITE_URL}/cookie-policy`,
    });

    const [policy, setPolicy] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchPolicy = async () => {
            try {
                const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/cookie-policies');
                let policyToSet = null;

                if (Array.isArray(result)) {
                    policyToSet = result[0];
                } else if (result.data) {
                    policyToSet = Array.isArray(result.data) ? result.data[0] : result.data;
                }

                if (ok && policyToSet) {
                    setPolicy(policyToSet);
                }
            } catch (error) {
                console.error('Error fetching cookie policy:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchPolicy();
    }, []);

    if (isLoading) {
        return (
            <div className="cookies-loading">
                <div className="loader"></div>
            </div>
        );
    }

    // Default data if none exists in API
    const displayData = policy || {
        hero_title_part: 'Cookie',
        hero_highlight_word: 'Policy',
        hero_description: 'We use cookies to enhance your experience and provide personalized service. Learn how we use them here.',
        last_updated_date: 'February 6, 2026',
        sections: []
    };

    return (
        <div className="cookies-container">
            <Navbar />

            <section className="cookies-hero">
                <div className="cookies-hero-bg-shape"></div>
                <motion.div
                    className="cookies-hero-content"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <h1>{displayData.hero_title_part} <span>{displayData.hero_highlight_word}</span></h1>
                    <p>{displayData.hero_description}</p>
                </motion.div>
            </section>

            <div className="cookies-content-wrapper">
                <motion.div
                    className="cookies-card"
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                >
                    {displayData.sections.map((section, index) => (
                        <div key={index} className="cookies-section">
                            <h2>{getIcon(section.icon_type)} {index + 1}. {section.section_title}</h2>
                            <p>{section.content}</p>
                            {section.list_items && section.list_items.length > 0 && (
                                <ul className="cookies-list">
                                    {section.list_items.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}

                    <div className="last-updated">
                        <span>Last Updated: {displayData.last_updated_date}</span>
                        <span onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ cursor: 'pointer', color: 'var(--accent-orange)', fontWeight: '600' }}>Back to top ↑</span>
                    </div>
                </motion.div>
            </div>

            <Footer />
        </div>
    );
};

export default CookiesPolicy;
